/*eslint-disable*/
import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    MenuItem,
    Select,
    FormControl,
    Grid,
    Box,
    CircularProgress,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart"; // Import BarChart
import { SelectChangeEvent } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import CustomBarChart from "./CustomBarChart";
import StatCard from "../LayoutComponents/StatCard";

import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const client = new FastAPIClient(config);

type MeetingLog = {
    user_id: string;
    first_name: string;
    last_name: string;
    date: string;
    meeting_type: string;
    meeting_type_description: string;
    team_name: string;
    warehouse_name: string;
    meeting_count: number;
};

interface UserMeetingProps {
    selectedUser: string;
    selectedTeam: string;
    selectedWarehouse: string;
    dateRange: [Dayjs | null, Dayjs | null];
}

const UserMeetings: React.FC<UserMeetingProps> = ({
    selectedUser,
    selectedTeam,
    selectedWarehouse,
    dateRange,
}) => {
    const [data, setData] = useState<MeetingLog[] | null>(null);

    useEffect(() => {
        fetchMeetings();
    }, []);

    const fetchMeetings = async () => {
        try {
            const response = await client.fetchUserMeetings(); // Fetch meetings data
            console.log("meetings", response.results);
            setData(response.results);
        } catch (error) {
            console.error("Error fetching meetings:", error);
        }
    };

    const uniqueTeams = useMemo(() => {
        if (!data) return [];
        const teams = new Set(data.map((d) => d.team_name).filter(Boolean));
        return Array.from(teams);
    }, [data]);

    const uniqueWarehouses = useMemo(() => {
        if (!data) return [];
        const warehouses = new Set(
            data.map((d) => d.warehouse_name).filter(Boolean)
        );
        return Array.from(warehouses);
    }, [data]);

    const uniqueUsers = useMemo(() => {
        if (!data) return [];
        const userMap = new Map<string, MeetingLog>();

        data.forEach((user) => {
            const fullName = `${user.first_name} ${user.last_name}`;
            if (!userMap.has(fullName)) {
                userMap.set(fullName, user);
            }
        });

        return Array.from(userMap.values());
    }, [data]);

    const filteredData = useMemo(() => {
        if (!data || !dateRange[0] || !dateRange[1]) return [];

        const [start, end] = dateRange;

        let filtered = data.filter((d) => {
            const inRange = dayjs(d.date).isBetween(start, end, null, "[]");
            const matchesTeam = selectedTeam === "all" || d.team_name === selectedTeam;
            const matchesUser = selectedUser === "all" || `${d.first_name} ${d.last_name}` === selectedUser;
            const matchesWarehouse = selectedWarehouse === "all" || d.warehouse_name === selectedWarehouse;

            return inRange && matchesTeam && matchesUser && matchesWarehouse;
        });

        // ❌ Exclude weekends
        filtered = filtered.filter((d) => {
            const day = dayjs(d.date).day(); // 0 = Sunday, 6 = Saturday
            return day !== 0 && day !== 6;
        });

        return filtered;
    }, [data, selectedUser, selectedTeam, selectedWarehouse, dateRange]);

    const chartData = useMemo(() => {
        if (!filteredData) return { labels: [], values: [], seriesLabels: [] };

        // Get unique, sorted, non-weekend dates from filteredData
        const allDates = Array.from(
            new Set(
                filteredData
                    .map((d) => dayjs(d.date))
                    .filter((d) => {
                        const day = d.day();
                        return day !== 0 && day !== 6;
                    })
                    .map((d) => d.format("YYYY-MM-DD"))
            )
        ).sort();

        // Create unique usernames
        const seriesLabels = uniqueUsers.map(
            (user) => `${user.first_name} ${user.last_name}`
        );

        const valueMap = new Map<string, Map<string, number>>();

        filteredData.forEach((entry) => {
            const date = dayjs(entry.date).format("YYYY-MM-DD");
            const username = `${entry.first_name} ${entry.last_name}`;
            const count = entry.meeting_count ?? 1;

            if (!valueMap.has(date)) {
                valueMap.set(date, new Map());
            }

            const dailyMap = valueMap.get(date)!;
            dailyMap.set(username, (dailyMap.get(username) || 0) + count);
        });

        const values = allDates.map((date) => {
            const dailyMap = valueMap.get(date) || new Map();
            return seriesLabels.map((username) => dailyMap.get(username) || 0);
        });

        return {
            labels: allDates,
            values,
            seriesLabels,
        };
    }, [filteredData, uniqueUsers]);

    const totalMeetings = useMemo(() => {
        if (!filteredData) return 0;
        return filteredData.reduce(
            (total, entry) => total + entry.meeting_count,
            0
        );
    }, [filteredData]);

    return (
        <StatCard
            title="Nombre de réunions"
            value={totalMeetings}
            subtitle=""
            loading={!data}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CustomBarChart
                    chartData={{
                        labels: chartData.labels,
                        values: chartData.values,
                        seriesLabels: chartData.seriesLabels,
                        showFullDateLabels: true,
                        showSeriesLabels: true,
                    }}
                />
            </LocalizationProvider>
        </StatCard>
    );
};

export default UserMeetings;
